import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "../../shadcn/ui/button";
import { Download, Smartphone, Monitor, Globe } from "lucide-react";

interface DeviceInfo {
  type: "ios" | "android" | "desktop" | "unknown";
  userAgent: string;
  isMobile: boolean;
}

const QRCodeScreen = () => {
  const history = useHistory();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // App Store URLs (replace with your actual app URLs)
  const APP_STORE_URL = "https://apps.apple.com/gh/app/spacia-work-stay-play/id6739746297";
  const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.lucid_array.spacia.individual&hl=en";

  // Fallback URL for desktop users - redirect to home
  const DESKTOP_URL = "/home";

  // Device detection function
  const detectDevice = (): DeviceInfo => {
    const userAgent = navigator.userAgent.toLowerCase();

    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isMobile = /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);

    let type: "ios" | "android" | "desktop" | "unknown" = "unknown";

    if (isIOS) {
      type = "ios";
    } else if (isAndroid) {
      type = "android";
    } else if (!isMobile) {
      type = "desktop";
    }

    return {
      type,
      userAgent,
      isMobile,
    };
  };

  // Generate appropriate URL based on device
  const generateAppStoreURL = (deviceType: string): string => {
    switch (deviceType) {
      case "ios":
        return APP_STORE_URL;
      case "android":
        return GOOGLE_PLAY_URL;
      case "desktop":
        return DESKTOP_URL;
      default:
        return DESKTOP_URL; // Fallback to desktop URL
    }
  };

  // Handle direct app store redirect
  const handleDirectRedirect = () => {
    if (!deviceInfo) return;

    const url = generateAppStoreURL(deviceInfo.type);
    if (deviceInfo.type === "desktop") {
      // For desktop, use React Router navigation
      history.push(url);
    } else {
      // For mobile, redirect to app store
      window.location.href = url;
    }
  };

  // Auto-redirect after a short delay
  useEffect(() => {
    const device = detectDevice();
    setDeviceInfo(device);
    setIsLoading(false);

    // Auto-redirect after 2 seconds
    const timer = setTimeout(() => {
      const url = generateAppStoreURL(device.type);
      if (device.type === "desktop") {
        // For desktop, use React Router navigation
        history.push(url);
      } else {
        // For mobile, redirect to app store
        window.location.href = url;
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [history]);

  // Get device icon
  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case "ios":
      case "android":
        return <Smartphone className="w-8 h-8" />;
      case "desktop":
        return <Monitor className="w-8 h-8" />;
      default:
        return <Globe className="w-8 h-8" />;
    }
  };

  // Get device name
  const getDeviceName = (deviceType: string) => {
    switch (deviceType) {
      case "ios":
        return "iOS Device";
      case "android":
        return "Android Device";
      case "desktop":
        return "Desktop";
      default:
        return "Unknown Device";
    }
  };

  // Get recommended store
  const getRecommendedStore = (deviceType: string) => {
    switch (deviceType) {
      case "ios":
        return "App Store";
      case "android":
        return "Google Play Store";
      case "desktop":
        return "Continue to Home";
      default:
        return "App Store";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Detecting your device...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-md mx-auto text-center">
        {/* Header */}
        <div className="mb-8">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {getDeviceIcon(deviceInfo?.type || "unknown")}
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Download Our App</h1>
          <p className="text-lg text-gray-600 mb-4">We detected a {getDeviceName(deviceInfo?.type || "unknown")}</p>
          <p className="text-sm text-gray-500">
            {deviceInfo?.type === "desktop"
              ? "Redirecting to home page in 2 seconds..."
              : `Redirecting to ${getRecommendedStore(deviceInfo?.type || "unknown")} in 2 seconds...`}
          </p>
        </div>

        {/* Download Button */}
        <div className="space-y-4">
          <Button onClick={handleDirectRedirect} className="w-full" size="lg" {...({} as any)}>
            <Download className="w-5 h-5 mr-2" />
            Download from {getRecommendedStore(deviceInfo?.type || "unknown")}
          </Button>

          <p className="text-xs text-gray-400">If you're not redirected automatically, click the button above</p>
        </div>

        {/* Manual Options */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-4">Or choose manually:</p>
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => window.open(APP_STORE_URL, "_blank")}
              className="text-sm"
              {...({} as any)}
            >
              App Store
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open(GOOGLE_PLAY_URL, "_blank")}
              className="text-sm"
              {...({} as any)}
            >
              Google Play
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeScreen;
